import { Filter } from 'lucide-react';

export type SegmentFilter = 'all' | 'always-none' | 'never-none' | 'selective';
export type PriceFilter = 'all' | 'low' | 'mid' | 'high';
export type ObjectionFilter = 'all' | 'has' | 'none';
export type SpendFilter = 'all' | 'nothing' | 'under50' | '50plus';
export type GoalFilter = 'all' | 'weight' | 'health' | 'strength' | 'nutrition' | 'activity' | 'stress';
export type AgeFilter = 'all' | '18-34' | '35-49' | '50plus';

interface Props {
  segment: SegmentFilter;
  price: PriceFilter;
  objection: ObjectionFilter;
  spend: SpendFilter;
  goal: GoalFilter;
  ageRange: AgeFilter;
  onSegmentChange: (v: SegmentFilter) => void;
  onPriceChange: (v: PriceFilter) => void;
  onObjectionChange: (v: ObjectionFilter) => void;
  onSpendChange: (v: SpendFilter) => void;
  onGoalChange: (v: GoalFilter) => void;
  onAgeChange: (v: AgeFilter) => void;
  totalCount: number;
  filteredCount: number;
  filterDescription: string;
}

export function BehavioralFilterBar({
  segment, price, objection, spend, goal, ageRange,
  onSegmentChange, onPriceChange, onObjectionChange,
  onSpendChange, onGoalChange, onAgeChange,
  totalCount, filteredCount, filterDescription,
}: Props) {
  const selectClass = 'bg-surface border border-border rounded-lg px-3 py-1.5 text-sm text-primary focus:outline-none focus:ring-2 focus:ring-chart-teal/30';

  return (
    <div className="card mb-4">
      <div className="flex flex-wrap items-center gap-3">
        <Filter size={14} className="text-muted" />
        <select value={segment} onChange={e => onSegmentChange(e.target.value as SegmentFilter)} className={selectClass}>
          <option value="all">All Segments</option>
          <option value="always-none">Always None (rejected everything)</option>
          <option value="never-none">Never None (always chose a product)</option>
          <option value="selective">Selective (mixed)</option>
        </select>

        <select value={price} onChange={e => onPriceChange(e.target.value as PriceFilter)} className={selectClass}>
          <option value="all">Any Price Preference</option>
          <option value="low">Primarily chose $499</option>
          <option value="mid">Primarily chose $999+</option>
          <option value="high">Primarily chose $1,499+</option>
        </select>

        <select value={objection} onChange={e => onObjectionChange(e.target.value as ObjectionFilter)} className={selectClass}>
          <option value="all">Any Price Reaction</option>
          <option value="has">Has price objection</option>
          <option value="none">No price objection</option>
        </select>

        <select value={spend} onChange={e => onSpendChange(e.target.value as SpendFilter)} className={selectClass}>
          <option value="all">Any Spending Level</option>
          <option value="nothing">Spends nothing on wellness</option>
          <option value="under50">Spends under $50/mo</option>
          <option value="50plus">Spends $50+/mo</option>
        </select>

        <select value={goal} onChange={e => onGoalChange(e.target.value as GoalFilter)} className={selectClass}>
          <option value="all">Any Health Goal</option>
          <option value="weight">Weight loss / body comp</option>
          <option value="health">General health / longevity</option>
          <option value="strength">Strength / athletic performance</option>
          <option value="nutrition">Nutrition / eating habits</option>
          <option value="activity">Activity / movement</option>
          <option value="stress">Stress / mental wellness</option>
        </select>

        <select value={ageRange} onChange={e => onAgeChange(e.target.value as AgeFilter)} className={selectClass}>
          <option value="all">Any Age</option>
          <option value="18-34">18–34</option>
          <option value="35-49">35–49</option>
          <option value="50plus">50+</option>
        </select>

        <span className="ml-auto text-xs text-muted">
          Showing <span className="font-semibold text-primary">{filteredCount.toLocaleString()}</span> of {totalCount.toLocaleString()} respondents
          {filterDescription && <span className="hidden sm:inline"> — {filterDescription}</span>}
        </span>
      </div>
    </div>
  );
}
